// External Imports
import flow from 'lodash/fp/flow';
import get from 'lodash/fp/get';
import mapValues from 'lodash/fp/mapValues';
import pick from 'lodash/fp/pick';
import uniq from 'lodash/uniq';
import sanitizeHtml from 'sanitize-html';

// Internal Imports
import { generateLazyLoadEmbed, isYoutubeEmbedLink } from './youtube.js';

// Local Variables
// All content item fields that can contain HTML with script tags.
const contentFields = ['content', 'excerpt'];

// Local Functions
// TODO: remove when USATECH-1712 gets addressed
// Some of our embeds for e.g. instagram do not include the protocol in their URL.
// Sanitize-html considers this to be invalid and fully removes them automatically.
// As a temporary solution, we are patching this here until we can try to address it
// in Wordpress in
// https://trello.com/c/6m99Z7ec/1712-spike-investigation-on-whether-we-could-solve-some-embeds-problems-in-wordpress-instead-of-the-frontend
// The regexp below matches a protocol-less URL inside of a src tag and adds https as the protocol
const addMissingHttpsProtocolToEmbedsURLs = (content) =>
  content.replaceAll(/"(\/\/(www\.)?([\dA-Za-z-]+\.)+[\dA-Za-z-][^"]*)"/g, 'https:$1');

const getUniqueValidScriptsUrl = (scripts) =>
  uniq(scripts.filter(Boolean).map(get(['attribs', 'src']))).filter(Boolean);

/**
 * Filter any script tags found. Add filtered scripts to collection.
 * For more documentation on this see docs/article-content-embeds.md
 *
 * @param {string} content
 * @return {string}
 */
const filterContentFields = (contentItem) => {
  const scripts = [];
  const lazyLoadEmbeds = {};

  const filterContent = (content) => {
    const lazyLoadReplaceMap = {};
    const toBeReplacedTagName = 'to-be-replaced-lazy-load-facade';
    const sanitizedHtml = sanitizeHtml(addMissingHttpsProtocolToEmbedsURLs(content), {
      allowedAttributes: {
        '*': ['*'],
        p: ['style'],
      },
      allowedTags: false,
      allowVulnerableTags: true,
      exclusiveFilter: (frame) => {
        if (frame.tag !== 'script') {
          return false;
        }
        scripts.push(frame);
        return true;
      },
      parseStyleAttributes: false,
      transformTags: {
        iframe: (tagName, attribs) => {
          if (isYoutubeEmbedLink(attribs.src)) {
            const { videoId, lazyLoadFacade, iframe } = generateLazyLoadEmbed({ attribs, tagName });
            lazyLoadEmbeds[videoId] = iframe;
            lazyLoadReplaceMap[videoId] = lazyLoadFacade;
            return {
              attribs: {
                'data-id': videoId,
              },
              tagName: toBeReplacedTagName,
            };
          }
          return {
            attribs,
            tagName,
          };
        },
      },
    });
    let htmlWithLazyLoadReplaced = sanitizedHtml;
    for (const [lazyLoadId, replacement] of Object.entries(lazyLoadReplaceMap)) {
      const regExp = new RegExp(
        `<${toBeReplacedTagName} data-id="${lazyLoadId}"></${toBeReplacedTagName}>`,
        'g',
      );
      htmlWithLazyLoadReplaced = htmlWithLazyLoadReplaced.replaceAll(regExp, replacement);
    }
    return htmlWithLazyLoadReplaced;
  };

  const filteredContent = flow(pick(contentFields), mapValues(filterContent))(contentItem);

  return {
    filteredContent,
    lazyLoadEmbeds,
    scripts: getUniqueValidScriptsUrl(scripts),
  };
};

// Module Exports
export { filterContentFields };
